import React, { useEffect, useState } from 'react'
import { Pressable, StyleSheet, View, type LayoutChangeEvent, type ViewStyle } from 'react-native'
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated'
import { DS } from '@/design-system'

export type ToggleTabOption<T extends string> = {
  value: T
  label: string
}

export type ToggleTabsProps<T extends string> = {
  options: ToggleTabOption<T>[]
  value: T
  onChange: (value: T) => void
  style?: ViewStyle
}

const CONTAINER_PADDING = 4
const ANIMATION_DURATION = 200

/**
 * Segmented-pill tab control used by lobby screens (lounge, tournament).
 * Renders nothing when there are fewer than two options — a single-tab strip
 * is visual noise. The active-tab highlight slides horizontally on change.
 */
export function ToggleTabs<T extends string>({ options, value, onChange, style }: ToggleTabsProps<T>) {
  const [trackWidth, setTrackWidth] = useState(0)
  const translateX = useSharedValue(0)

  const activeIndex = Math.max(
    0,
    options.findIndex((o) => o.value === value)
  )
  const tabWidth = options.length > 0 ? (trackWidth - CONTAINER_PADDING * 2) / options.length : 0

  // Drive the highlight from the active index inside `useEffect` instead of
  // writing to the shared value during render. Render-phase writes to a
  // shared value are undefined behavior in Reanimated (can drop the first
  // frame, fire on remount, or restart the timing animation on every
  // parent re-render — which is exactly what the per-second lobby tick
  // would have caused). Gating on `trackWidth > 0` avoids the first-paint
  // `withTiming(0)` no-op that obscured the actual transition.
  useEffect(() => {
    if (tabWidth <= 0) return
    translateX.value = withTiming(activeIndex * tabWidth, { duration: ANIMATION_DURATION })
  }, [activeIndex, tabWidth, translateX])

  const highlightStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: translateX.value }],
    width: tabWidth
  }))

  const onLayout = (e: LayoutChangeEvent) => {
    const w = e.nativeEvent.layout.width
    if (w !== trackWidth) setTrackWidth(w)
  }

  if (options.length < 2) return null

  return (
    <View style={[styles.container, style]} onLayout={onLayout}>
      {/* Sliding highlight — only rendered once we know the track width to
          avoid a jump from x=0 on the first paint. */}
      {trackWidth > 0 && <Animated.View pointerEvents="none" style={[styles.highlight, highlightStyle]} />}
      {options.map((opt) => {
        const active = opt.value === value
        return (
          <Pressable
            key={opt.value}
            onPress={() => onChange(opt.value)}
            accessibilityRole="tab"
            accessibilityState={{ selected: active }}
            accessibilityLabel={opt.label}
            style={styles.tab}
          >
            <DS.Text
              variant={active ? 'Body Small/Medium' : 'Body Small/Regular'}
              style={active ? styles.labelActive : styles.label}
            >
              {opt.label}
            </DS.Text>
          </Pressable>
        )
      })}
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    backgroundColor: 'rgba(221, 218, 210, 0.4)',
    height: 45,
    padding: CONTAINER_PADDING,
    borderRadius: 8,
    marginHorizontal: 24,
    alignItems: 'center'
  },
  highlight: {
    position: 'absolute',
    top: CONTAINER_PADDING,
    left: CONTAINER_PADDING,
    bottom: CONTAINER_PADDING,
    backgroundColor: DS.palette['Global/White'],
    borderWidth: 1,
    borderColor: DS.palette['Brand/Light Grey'],
    borderRadius: 8
  },
  tab: {
    flex: 1,
    height: 37,
    paddingHorizontal: 16,
    paddingVertical: 4,
    alignItems: 'center',
    justifyContent: 'center'
  },
  label: {
    color: DS.palette['Global/Black'],
    fontWeight: '400'
  },
  labelActive: {
    color: DS.palette['Global/Black'],
    fontWeight: '600'
  }
})
